import { Icon } from "@components/library";
import { Tooltip } from "@components/TippyTooltip/Tooltip";
import { TooltipContent } from "@components/TippyTooltip/TooltipContent";
import { COLORS, FONTS } from "@constants";
import { useTypingAnimation } from "@hooks/useTypingAnimation";
import type { DiscoverFeedInterest } from "@tsTypes/discover_feed_interests/_discover_feed_interest";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface InterestBadgeProps {
  interest: DiscoverFeedInterest;
  onClick: (interest: DiscoverFeedInterest, element: HTMLElement) => void;
}

const MAX_TITLE_PREVIEW_LENGTH = 80;

const InterestBadge = ({ interest, onClick }: InterestBadgeProps) => {
  const { ai_title, description } = interest;
  const title =
    ai_title ??
    (description && description.length > MAX_TITLE_PREVIEW_LENGTH
      ? description.substring(0, MAX_TITLE_PREVIEW_LENGTH) + "..."
      : description);

  const prevTextRef = useRef(title);
  const isFirstRenderRef = useRef(true);
  const wasAnimatingRef = useRef(false);

  const [animationState, setAnimationState] = useState<{
    oldText: string;
    newText: string;
  } | null>(null);
  const [showEntranceAnimation, setShowEntranceAnimation] = useState(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      const timer = setTimeout(() => setShowEntranceAnimation(false), 400);
      return () => clearTimeout(timer);
    }

    if (prevTextRef.current !== title) {
      setAnimationState({
        oldText: prevTextRef.current ?? "",
        newText: title ?? "",
      });

      prevTextRef.current = title;
    }
  }, [title]);

  const { displayedText, isAnimating } = useTypingAnimation(
    animationState?.oldText ?? "",
    animationState?.newText ?? "",
    Boolean(animationState)
  );

  useEffect(() => {
    if (isAnimating) {
      wasAnimatingRef.current = true;
      return;
    }

    if (animationState && wasAnimatingRef.current) {
      wasAnimatingRef.current = false;
      setAnimationState(null);
    }
  }, [isAnimating, animationState]);

  const badgeText = animationState ? displayedText : title ?? "";

  return (
    <Tooltip
      placement="top"
      offset={[0, 10]}
      render={(attrs) => (
        <TooltipContent
          font={FONTS.REGULAR_3}
          width="181px"
          data-placement={attrs["data-placement"]}
        >
          Edit or remove this interest
        </TooltipContent>
      )}
    >
      <StyledBadge
        onClick={(e) => onClick(interest, e.currentTarget)}
        $showEntrance={showEntranceAnimation}
      >
        {badgeText}
        <Icon name="Edit" size="xs" color={COLORS.BLUE_600} />
      </StyledBadge>
    </Tooltip>
  );
};

export default InterestBadge;

const StyledBadge = styled.button<{ $showEntrance: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 6px 14px 7px;
  background-color: ${COLORS.BLUE_LIGHT_300};
  border: 1px solid ${COLORS.BLUE_600};
  border-radius: 100px;
  ${FONTS.MEDIUM_3};
  color: ${COLORS.BLUE_600};
  cursor: pointer;
`;
